import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CallLogList from "@/components/CallLogList";
import {
  ClipboardPaste,
  Delete,
  Phone,
} from "lucide-react";

// Keep what a phone number can contain; pasted text often has spaces, dashes
// or words around it ("Call me on 98123-40001").
function cleanNumber(text) {
  const t = (text || "").replace(/[^\d+]/g, "");
  return t.startsWith("+") ? "+" + t.slice(1).replace(/\+/g, "") : t.replace(/\+/g, "");
}

// Same rule as the server: an Indian mobile (10 digits, or with 0 / 91 in
// front) or a full international number starting with +.
function isDialable(n) {
  const d = n.replace(/\D/g, "");
  if (n.startsWith("+")) return d.length >= 11 && d.length <= 15;
  return d.length === 10 || (d.length === 11 && d.startsWith("0")) || (d.length === 12 && d.startsWith("91"));
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0", "⌫"];

function Dialer({ onCalled }) {
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = isDialable(number);

  const press = (k) => {
    if (k === "⌫") setNumber((n) => n.slice(0, -1));
    else if (k === "+") setNumber((n) => (n ? n : "+"));
    else setNumber((n) => (n.length < 16 ? n + k : n));
  };

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const n = cleanNumber(text);
      if (n) setNumber(n);
      else toast.error("No phone number on the clipboard.");
    } catch {
      toast.error("Long-press the number box and choose Paste.");
    }
  };

  const call = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/calls/initiate", { dial_number: number });
      toast.success(data.message || "Your phone will ring in a few seconds.");
      setNumber("");
      onCalled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start call.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-none" data-testid="emp-dialer">
      <CardContent className="p-3 space-y-3">
        <div className="flex gap-2">
          <Input
            type="tel"
            inputMode="tel"
            placeholder="Type or paste number"
            value={number}
            onChange={(e) => setNumber(cleanNumber(e.target.value))}
            onKeyDown={(e) => e.key === "Enter" && call()}
            className="mono text-base tracking-wider h-11"
            data-testid="emp-dialer-input"
          />
          <Button
            type="button"
            variant="outline"
            className="h-11 shrink-0"
            onClick={paste}
            title="Paste number"
            data-testid="emp-dialer-paste"
          >
            <ClipboardPaste className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              className="h-11 rounded-lg bg-slate-50 hover:bg-slate-100 active:bg-slate-200 mono text-lg grid place-items-center"
              data-testid={`emp-dialer-key-${k === "⌫" ? "back" : k}`}
            >
              {k === "⌫" ? <Delete className="w-5 h-5" /> : k}
            </button>
          ))}
        </div>
        <Button
          type="button"
          className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
          disabled={!valid || busy}
          onClick={call}
          data-testid="emp-dialer-call"
        >
          <Phone className="w-4 h-4 mr-2" />
          {busy ? "Calling…" : "Call"}
        </Button>
        <p className="text-[11px] text-slate-500 text-center">
          Your phone rings first; answer it to connect the customer. The call
          goes through the company number and is recorded.
        </p>
      </CardContent>
    </Card>
  );
}

export default function EmpCalls() {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const load = () => api.get("/calls").then(({ data }) => setCalls(data));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="p-4 space-y-4" data-testid="emp-calls">
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#FF5000]">
          Dialer
        </div>
        <h1 className="font-heading text-2xl font-bold mt-1">Call a number</h1>
      </div>

      <Dialer onCalled={load} />

      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#FF5000]">
          Call log
        </div>
        <h2 className="font-heading text-xl font-bold mt-1">Calls</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Your calls, plus missed calls from customers. Tap the phone button to call back.
        </p>
      </div>

      <CallLogList
        calls={calls}
        onCalled={load}
        canCreateTask={!!user?.can_create_tasks}
        selfId={user?.id}
      />
    </div>
  );
}
